/**
 * E07-F07-T04 — REAL adapter for the global Do-Not-Source list (EER clients).
 *
 * Replaces the prototype mock (`../mock/dnsClients.ts`) now that the endpoints
 * exist (E07-F07-T01 / T02, routes/admin-dns-clients.ts):
 *
 *   GET   /admin/dns-clients                     → useDnsClients()          any signed-in user
 *   POST  /admin/dns-clients/preview             → usePreviewDnsPaste()     admin
 *   POST  /admin/dns-clients                     → useAddDnsClients()       admin
 *   POST  /admin/dns-clients/:id/remove          → useRemoveDnsClient()     admin
 *   POST  /admin/dns-clients/:id/restore         → useRestoreDnsClient()    admin
 *   PATCH /admin/dns-clients/:id                 → useUpdateDnsClient()     admin (409 on key clash)
 *   POST  /admin/dns-clients/pull-from-projects/preview → usePreviewDnsPull() admin (E07-F07-T06)
 *   POST  /admin/dns-clients/pull-from-projects  → usePullDnsFromProjects() admin { clientIds }
 *
 * Same shape as the sibling modules: shared `apiClient` (Bearer token, envelope
 * normalization, never throws), `unwrap` at the Query boundary, and every body
 * parsed against the `@eer/shared` contract. Hook signatures are identical to the
 * mock, so DnsClientsTab.tsx did not change.
 *
 * The server is the authority on a paste: preview saves nothing, add re-parses the
 * text and saves only lines that are new at that moment. Writes are never retried
 * (one click = one write).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  dnsAddResponseSchema,
  dnsClientEntrySchema,
  dnsClientsListResponseSchema,
  dnsPastePreviewResponseSchema,
  dnsPullPreviewResponseSchema,
  dnsPullResponseSchema,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../../api/client.js';
import type {
  DnsClientEntry,
  DnsClientUpdate,
  DnsClientsList,
  DnsPastePreview,
  DnsPullPreview,
  DnsPullResult,
} from '../dnsClientsModel.js';

/** The legacy command label the API logs requests under (sibling parity). */
const COMMAND = 'manage_dns_clients';

// ---------------------------------------------------------------------------
// Query keys (feature-local)
// ---------------------------------------------------------------------------

export const DNS_CLIENTS_QUERY_KEY = ['admin', 'dns-clients'] as const;

function useInvalidate(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: DNS_CLIENTS_QUERY_KEY });
}

function entryPath(id: string): string {
  return `/admin/dns-clients/${encodeURIComponent(id)}`;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

async function fetchDnsClients(): Promise<DnsClientsList> {
  const result = await apiClient.get<unknown>('/admin/dns-clients', { command: COMMAND });
  return dnsClientsListResponseSchema.parse(unwrap(result));
}

async function postPreview(text: string): Promise<DnsPastePreview> {
  const result = await apiClient.post<unknown>(
    '/admin/dns-clients/preview',
    { text },
    { command: COMMAND },
  );
  return dnsPastePreviewResponseSchema.parse(unwrap(result));
}

async function postAdd(text: string): Promise<{ added: number }> {
  const result = await apiClient.post<unknown>(
    '/admin/dns-clients',
    { text },
    { command: COMMAND },
  );
  return dnsAddResponseSchema.parse(unwrap(result));
}

async function postSetActive(id: string, active: boolean): Promise<DnsClientEntry> {
  const result = await apiClient.post<unknown>(
    `${entryPath(id)}/${active ? 'restore' : 'remove'}`,
    {},
    { command: COMMAND },
  );
  return dnsClientEntrySchema.parse(unwrap(result));
}

async function patchEntry(id: string, body: DnsClientUpdate): Promise<DnsClientEntry> {
  const result = await apiClient.patch<unknown>(
    entryPath(id),
    { name: body.name, domain: body.domain },
    { command: COMMAND },
  );
  return dnsClientEntrySchema.parse(unwrap(result));
}

async function postPullPreview(): Promise<DnsPullPreview> {
  const result = await apiClient.post<unknown>(
    '/admin/dns-clients/pull-from-projects/preview',
    {},
    { command: COMMAND },
  );
  return dnsPullPreviewResponseSchema.parse(unwrap(result));
}

async function postPull(clientIds: readonly number[]): Promise<DnsPullResult> {
  const result = await apiClient.post<unknown>(
    '/admin/dns-clients/pull-from-projects',
    { clientIds: [...clientIds] },
    { command: COMMAND },
  );
  return dnsPullResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** The whole list (active + removed) with counters. Readable by every signed-in role. */
export function useDnsClients(): UseQueryResult<DnsClientsList, ApiResultError> {
  return useQuery<DnsClientsList, ApiResultError>({
    queryKey: DNS_CLIENTS_QUERY_KEY,
    queryFn: fetchDnsClients,
  });
}

// ---------------------------------------------------------------------------
// Writes (admin only — the server enforces requireRole('admin'))
// ---------------------------------------------------------------------------

/** Group a paste into new / already in list / unrecognized. Saves nothing. */
export function usePreviewDnsPaste(): UseMutationResult<DnsPastePreview, ApiResultError, string> {
  return useMutation<DnsPastePreview, ApiResultError, string>({
    mutationFn: postPreview,
    retry: false,
  });
}

/** Add the NEW lines of a paste (the server re-parses; duplicates are skipped). */
export function useAddDnsClients(): UseMutationResult<{ added: number }, ApiResultError, string> {
  const invalidate = useInvalidate();
  return useMutation<{ added: number }, ApiResultError, string>({
    mutationFn: postAdd,
    retry: false,
    onSuccess: () => invalidate(),
  });
}

/** Soft delete (`isActive=false`). */
export function useRemoveDnsClient(): UseMutationResult<DnsClientEntry, ApiResultError, string> {
  const invalidate = useInvalidate();
  return useMutation<DnsClientEntry, ApiResultError, string>({
    mutationFn: (id) => postSetActive(id, false),
    retry: false,
    onSuccess: () => invalidate(),
  });
}

/** Bring a removed entry back. */
export function useRestoreDnsClient(): UseMutationResult<DnsClientEntry, ApiResultError, string> {
  const invalidate = useInvalidate();
  return useMutation<DnsClientEntry, ApiResultError, string>({
    mutationFn: (id) => postSetActive(id, true),
    retry: false,
    onSuccess: () => invalidate(),
  });
}

/** Fix a name / domain. A domain another entry already holds is rejected (409). */
export function useUpdateDnsClient(): UseMutationResult<
  DnsClientEntry,
  ApiResultError,
  { readonly id: string; readonly body: DnsClientUpdate }
> {
  const invalidate = useInvalidate();
  return useMutation<
    DnsClientEntry,
    ApiResultError,
    { readonly id: string; readonly body: DnsClientUpdate }
  >({
    mutationFn: ({ id, body }) => patchEntry(id, body),
    retry: false,
    onSuccess: () => invalidate(),
  });
}

/** What «Pull clients from projects» would add — saves nothing (E07-F07-T06). */
export function usePreviewDnsPull(): UseMutationResult<DnsPullPreview, ApiResultError, void> {
  return useMutation<DnsPullPreview, ApiResultError, void>({
    mutationFn: postPullPreview,
    retry: false,
  });
}

/**
 * Add the project clients the admin confirmed in the preview (`clientIds`).
 * Idempotent; a key the admin removed earlier stays removed.
 */
export function usePullDnsFromProjects(): UseMutationResult<
  DnsPullResult,
  ApiResultError,
  readonly number[]
> {
  const invalidate = useInvalidate();
  return useMutation<DnsPullResult, ApiResultError, readonly number[]>({
    mutationFn: postPull,
    retry: false,
    onSuccess: () => invalidate(),
  });
}
